/**
 * Theme Provider Component
 *
 * Provides theme and branding configuration to the entire application via React Context.
 * Loads theme based on the store label from runtime config.
 */

import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import type { ThemeContextValue, ThemeConfig, BrandingConfig, ThemeMode } from "./themeTypes";
import { loadThemeConfig, loadBrandingConfig, injectThemeVariables } from "./themeLoader";
import { fetchRuntimeConfig } from "../services/config";

const THEME_MODE_STORAGE_KEY = "intersum-storefront:theme-mode";

/**
 * Theme Context
 * Provides theme and branding to all components
 */
const ThemeContext = createContext<ThemeContextValue>({
  theme: null,
  branding: null,
  isLoading: true,
  error: null,
  themeMode: "light",
  setThemeMode: () => {
    /* no-op default; real setter provided by ThemeProvider */
  },
  supportsDarkMode: false,
});

/**
 * Hook to access theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

interface ThemeProviderProps {
  children: ReactNode;
}

function readInitialMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage unavailable (private mode, etc.) — fall through
  }
  if (typeof window.matchMedia === "function") {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  }
  return "light";
}

/**
 * Theme Provider Component
 * Loads theme and branding based on store label from runtime config.
 * Manages a themeMode state and persists user's choice across sessions.
 */
export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const [theme, setTheme] = useState<ThemeConfig | null>(null);
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => readInitialMode());

  useEffect(() => {
    void loadThemeAndBranding();
  }, []);

  // Re-inject CSS variables whenever theme or mode changes
  useEffect(() => {
    if (!theme) return;
    const supports = Boolean(theme.darkMode);
    const effectiveMode: ThemeMode = supports ? themeMode : "light";
    injectThemeVariables(theme, effectiveMode);
  }, [theme, themeMode]);

  async function loadThemeAndBranding(): Promise<void> {
    try {
      setIsLoading(true);
      setError(null);

      console.log("[ThemeProvider] Loading runtime config...");
      const runtimeConfig = await fetchRuntimeConfig();
      const storeLabel = runtimeConfig.storeLabel;
      console.log("[ThemeProvider] Store label:", storeLabel);

      const [themeConfig, brandingConfig] = await Promise.all([
        loadThemeConfig(storeLabel),
        loadBrandingConfig(storeLabel),
      ]);

      console.log("[ThemeProvider] Theme loaded:", themeConfig.name);
      console.log("[ThemeProvider] Branding loaded:", brandingConfig.store?.name ?? brandingConfig.name);

      setTheme(themeConfig);
      setBranding(brandingConfig);
      setIsLoading(false);
    } catch (err) {
      console.error("[ThemeProvider] Failed to load theme:", err);
      setError(err instanceof Error ? err : new Error("Unknown error loading theme"));
      setIsLoading(false);
    }
  }

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    try {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore persistence failure
    }
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-800" />
          <p className="mt-4 text-gray-600">Loading theme...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-4" aria-hidden="true">
            ⚠️
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Theme Loading Error</h1>
          <p className="text-gray-600 mb-4">Failed to load theme configuration.</p>
          <p className="text-sm text-gray-500 font-mono bg-gray-100 p-3 rounded">{error.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-gray-800 text-white rounded hover:bg-gray-900 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const supportsDarkMode = Boolean(theme?.darkMode);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        branding,
        isLoading,
        error,
        themeMode: supportsDarkMode ? themeMode : "light",
        setThemeMode,
        supportsDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Export ThemeContext for advanced use cases
 */
export { ThemeContext };
